import { format, isValid, parseISO } from "date-fns";

export const COLUMN_VISIBILITY_KEY = "girinestham.volunteerMembershipRenewal.columnVisibility";
export const TRANSACTION_ID_MAX_LENGTH = 20;
export const PAGE_SIZE = 20;

export const RENEWAL_LIST_COLUMN_DEFS = [
  { id: "application_code", label: "Application code", defaultVisible: true, sortable: true },
  { id: "student_code", label: "Student code", defaultVisible: true, sortable: true },
  { id: "student_name", label: "Student name", defaultVisible: true, sortable: true },
  { id: "membership_name", label: "Membership", defaultVisible: true, sortable: true },
  { id: "start_date", label: "Start date", defaultVisible: true, sortable: true },
  { id: "end_date", label: "End date", defaultVisible: true, sortable: true },
  { id: "application_verified", label: "Application verified", defaultVisible: true, sortable: true },
  { id: "payment_verified", label: "Payment verified", defaultVisible: true, sortable: true },
  { id: "amount", label: "Amount", defaultVisible: true, sortable: true },
  { id: "submitted_at", label: "Submitted at", defaultVisible: true, sortable: true },
];

export const COLUMN_DEFS_FOR_FILTERS = RENEWAL_LIST_COLUMN_DEFS.map((c) => ({ id: c.id, label: c.label }));

export function loadRenewalListColumnVisibility() {
  try {
    const raw = localStorage.getItem(COLUMN_VISIBILITY_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    /* ignore */
  }
  return null;
}

export function createInitialRenewalListColumnVisibility() {
  const saved = loadRenewalListColumnVisibility();
  const initial = {};
  for (const c of RENEWAL_LIST_COLUMN_DEFS) {
    initial[c.id] = saved?.[c.id] ?? c.defaultVisible;
  }
  return initial;
}

export function isTruthy(v) {
  return v === true || v === 1 || v === "1";
}

export function formatDateCell(value, withTime = false) {
  if (!value) return "—";
  try {
    const raw = String(value).trim();
    let d = parseISO(raw);
    if (!isValid(d) && /^\d{4}-\d{2}-\d{2}/.test(raw)) d = new Date(raw.replace(" ", "T"));
    if (!isValid(d)) d = new Date(raw);
    if (!isValid(d)) return "—";
    return format(d, withTime ? "PPp" : "PP");
  } catch {
    return "—";
  }
}

export function formatMoney(v) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  return Number.isFinite(n) ? `₹${n.toFixed(2)}` : "—";
}

const IST_TIMEZONE = "Asia/Kolkata";

/** Calendar date YYYY-MM-DD in IST (for renewal start/end payloads). */
export function toISTDateOnly(value) {
  if (value == null || value === "") return "";
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const d =
    value instanceof Date
      ? value
      : new Date(/^\d{4}-\d{2}-\d{2}/.test(raw) ? raw.replace(" ", "T") : raw);
  if (Number.isNaN(d.getTime())) {
    const pre = raw.match(/^(\d{4}-\d{2}-\d{2})/);
    return pre ? pre[1] : "";
  }
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: IST_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
}

export function formatDateOnly(date) {
  return toISTDateOnly(date);
}

export function addDays(dateString, days) {
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return "";
  d.setDate(d.getDate() + Number(days || 0));
  return formatDateOnly(d);
}

export function toMoneyNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

export function calcDiscountedSubscriptionFee(price, discountPct) {
  const safePrice = Math.max(0, toMoneyNumber(price));
  const safeDiscount = Math.min(100, Math.max(0, toMoneyNumber(discountPct)));
  return safePrice - (safePrice * safeDiscount) / 100;
}

export function calcRenewalAmount(row) {
  if (!row) return 0;
  const hasPlanPricing = row.membership_price != null || row.membership_discount != null;
  if (hasPlanPricing) {
    return calcDiscountedSubscriptionFee(row.membership_price, row.membership_discount);
  }
  return Math.max(0, toMoneyNumber(row.amount));
}

export function renewalListSortValue(row, id) {
  if (id === "application_verified" || id === "payment_verified") return isTruthy(row[id]) ? 1 : 0;
  if (id === "amount") return Number(row[id] || 0);
  if (id === "start_date" || id === "end_date" || id === "submitted_at") {
    const t = new Date(row[id] || 0).getTime();
    return Number.isFinite(t) ? t : 0;
  }
  return String(row[id] ?? "").toLowerCase();
}

export function defaultMembershipRenewListFilters() {
  return {
    student_search: "",
    column: "",
    value: "",
  };
}

export function renewalDirectoryCellDisplay(c, row) {
  if (c.id === "application_verified" || c.id === "payment_verified") {
    return isTruthy(row[c.id]) ? "Verified" : "Not verified";
  }
  if (c.id === "amount") return String(calcRenewalAmount(row));
  if (c.id === "start_date" || c.id === "end_date") return String(formatDateCell(row[c.id]) ?? "");
  if (c.id === "submitted_at") return String(formatDateCell(row.submitted_at, true) ?? "");
  return row[c.id] != null && row[c.id] !== "" ? String(row[c.id]) : "";
}
